import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { AlertTriangle, ArrowUpRight, X } from "lucide-react";

const T = {
  fr: { reached: "Vous avez atteint votre limite", near: "Vous approchez de votre limite", cta: "Passer à un plan supérieur",
        animals: "animaux", users: "utilisateurs", elevages: "élevages", ai: "générations IA ce mois", of: "sur" },
  en: { reached: "You've reached your limit", near: "You're close to your limit", cta: "Upgrade your plan",
        animals: "animals", users: "users", elevages: "livestock", ai: "AI generations this month", of: "of" },
  es: { reached: "Has alcanzado tu límite", near: "Estás cerca de tu límite", cta: "Mejorar tu plan",
        animals: "animales", users: "usuarios", elevages: "granjas", ai: "generaciones IA este mes", of: "de" },
  it: { reached: "Hai raggiunto il tuo limite", near: "Ti stai avvicinando al limite", cta: "Passa a un piano superiore",
        animals: "animali", users: "utenti", elevages: "allevamenti", ai: "generazioni IA questo mese", of: "su" },
  de: { reached: "Sie haben Ihr Limit erreicht", near: "Sie nähern sich Ihrem Limit", cta: "Upgrade durchführen",
        animals: "Tiere", users: "Benutzer", elevages: "Bestände", ai: "KI-Generierungen diesen Monat", of: "von" },
  nl: { reached: "U heeft uw limiet bereikt", near: "U nadert uw limiet", cta: "Upgraden naar een hoger plan", animals: "dieren", users: "gebruikers", elevages: "bedrijven", ai: "AI-generaties deze maand", of: "van" },
  sv: { reached: "Du har nått din gräns", near: "Du närmar dig din gräns", cta: "Uppgradera din plan", animals: "djur", users: "användare", elevages: "gårdar", ai: "AI-genereringar denna månad", of: "av" },
  da: { reached: "Du har nået din grænse", near: "Du nærmer dig din grænse", cta: "Opgrader til en højere plan", animals: "dyr", users: "brugere", elevages: "besætninger", ai: "AI-genereringer denne måned", of: "af" },
  bg: { reached: "Достигнахте лимита си", near: "Приближавате се до лимита си", cta: "Преминете към по-висок план", animals: "животни", users: "потребители", elevages: "ферми", ai: "ИИ генерации този месец", of: "от" },
  el: { reached: "Έχετε φτάσει το όριό σας", near: "Πλησιάζετε το όριό σας", cta: "Αναβάθμιση πλάνου", animals: "ζώα", users: "χρήστες", elevages: "εκτροφεία", ai: "δημιουργίες ΑΙ αυτόν τον μήνα", of: "από" },
  hr: { reached: "Dosegli ste svoj limit", near: "Približavate se svom limitu", cta: "Nadogradite plan", animals: "životinja", users: "korisnika", elevages: "farmi", ai: "AI generacija ovaj mjesec", of: "od" },
  et: { reached: "Olete jõudnud oma limiidini", near: "Lähenete oma limiidile", cta: "Uuenda plaani", animals: "loomad", users: "kasutajad", elevages: "farmid", ai: "AI genereerimised sel kuul", of: "/" },
  fi: { reached: "Olet saavuttanut rajasi", near: "Olet lähestymässä rajaasi", cta: "Päivitä ylempään suunnitelmaan", animals: "eläimet", users: "käyttäjät", elevages: "tilat", ai: "AI-luomukset tässä kuussa", of: "/" },
  hu: { reached: "Elérte a korlátját", near: "Közeledik a korlátjához", cta: "Váltson magasabb csomagra", animals: "állatok", users: "felhasználók", elevages: "tenyészetek", ai: "AI generációk ebben a hónapban", of: "/" },
  lv: { reached: "Jūs esat sasniedzis savu limitu", near: "Jūs tuvojaties savam limitam", cta: "Pāriet uz augstāku plānu", animals: "dzīvnieki", users: "lietotāji", elevages: "fermas", ai: "AI ģenerācijas šomēnes", of: "no" },
  lt: { reached: "Pasiekėte savo limitą", near: "Artėjate prie savo limito", cta: "Pereiti į aukštesnį planą", animals: "gyvūnai", users: "vartotojai", elevages: "ūkiai", ai: "IA generavimai šį mėnesį", of: "iš" },
  pl: { reached: "Osiągnąłeś swój limit", near: "Zbliżasz się do swojego limitu", cta: "Przejdź na wyższy plan", animals: "zwierzęta", users: "użytkownicy", elevages: "hodowle", ai: "generacje AI w tym miesiącu", of: "z" },
  pt: { reached: "Você atingiu seu limite", near: "Você está se aproximando do seu limite", cta: "Fazer upgrade de plano", animals: "animais", users: "utilizadores", elevages: "criações", ai: "gerações de IA este mês", of: "de" },
  ro: { reached: "Ați atins limita", near: "Vă apropiați de limită", cta: "Treceți la un plan superior", animals: "animale", users: "utilizatori", elevages: "ferme", ai: "generări IA luna aceasta", of: "din" },
  sk: { reached: "Dosiahli ste svoj limit", near: "Blížite sa k svojmu limitu", cta: "Prejsť na vyšší plán", animals: "zvieratá", users: "používatelia", elevages: "chovy", ai: "AI generácie tento mesiac", of: "z" },
  sl: { reached: "Dosegli ste svojo omejitev", near: "Približujete se svoji omejitvi", cta: "Nadgradite na višji paket", animals: "živali", users: "uporabniki", elevages: "reje", ai: "generiranja UI ta mesec", of: "od" },
  cs: { reached: "Dosáhli jste svého limitu", near: "Blížíte se svému limitu", cta: "Přejít na vyšší plán", animals: "zvířata", users: "uživatelé", elevages: "chovy", ai: "AI generování tento měsíc", of: "z" },
};

export default function UpgradeBanner() {
  const { farm } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const tr = T[lang] || T.fr;

  useEffect(() => {
    if (!farm) return;
    api.get("/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, [farm]);

  if (!usage || dismissed) return null;

  const m = usage.metrics || {};
  const worst = Object.entries(m)
    .map(([key, v]) => ({ key, ...v, ratio: v.limit > 0 ? v.used / v.limit : 0 }))
    .filter((x) => x.limit > 0 && x.ratio >= 0.8)
    .sort((a, b) => b.ratio - a.ratio)[0];

  if (!worst) return null;

  const atLimit = worst.used >= worst.limit;

  return (
    <div
      data-testid="upgrade-banner"
      className={`mb-6 rounded-2xl border px-4 py-3 flex items-center gap-3 ${
        atLimit ? "border-destructive/40 bg-destructive/5" : "border-accent/40 bg-accent/5"
      }`}
    >
      <div className={`h-9 w-9 shrink-0 grid place-items-center rounded-xl ${atLimit ? "bg-destructive/10 text-destructive" : "bg-accent/10 text-accent"}`}>
        <AlertTriangle className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight">
          {atLimit ? tr.reached : tr.near}
        </p>
        <p className="text-xs text-muted-foreground">
          {worst.used} {tr.of} {worst.limit} {tr[worst.key]}
        </p>
      </div>
      <button
        data-testid="upgrade-banner-cta"
        onClick={() => navigate("/app/abonnement")}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold px-4 py-2 hover:opacity-90 transition-opacity"
      >
        {tr.cta} <ArrowUpRight className="h-3.5 w-3.5" />
      </button>
      <button
        data-testid="upgrade-banner-dismiss"
        onClick={() => setDismissed(true)}
        className="shrink-0 h-8 w-8 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
